import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  StopOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import CTable from "components/CTable";
import {
  getGridify,
  showConfirm,
  showError,
  showSuccess,
  useNewState,
} from "helpers/utility";
import useAjax from "helpers/ajax";
import { DEFAULT_PAGE_SIZE } from "constants";
import CSelect from "components/CSelect";
import confirm from "antd/lib/modal/confirm";
import { useNavigate } from "react-router-dom";
import DeviceInfo from "./deviceInfo";

const { Option } = Select;

const LicenseActivations = () => {
  const { get, put } = useAjax();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useNewState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [form] = Form.useForm();
  const [deviceInfo, setDeviceInfo] = useNewState({ visible: false, data: [] });

  const onActivate = (record) => {
    const msg = record.active ? "deactivate" : "activate";
    showConfirm(`Do you want to ${msg}?`, "License activation").then(() => {
      put("api/v1/licenses/activate", {
        key: record.licenseID,
        active: !record.active,
      })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const showRenewConfirm = (days) =>
    new Promise((resolve) => {
      confirm({
        icon: <ExclamationCircleOutlined />,
        title: "Renew license",
        content: (
          <div>
            It will extend the license's expiry to specified number of days.
            <Form
              layout="inline"
              form={form}
              initialValues={{ noOfDays: days }}
            >
              <Form.Item
                name="noOfDays"
                label="No of days"
                rules={[{ required: true }]}
              >
                <InputNumber precision={0} autoFocus />
              </Form.Item>
            </Form>
          </div>
        ),
        okText: "Yes",
        okType: "danger",
        cancelText: "No",
        onOk() {
          form.validateFields().then((values) => {
            const { noOfDays } = values;
            return resolve({ noOfDays });
          });
        },
        onCancel() {},
      });
    });

  const onRenewLicense = (record) => {
    showRenewConfirm(record.noOfDays).then(({ noOfDays }) => {
      get("api/v1/licenses/renew", {
        licenseID: record.licenseID,
        days: noOfDays,
      })
        .then(() => {
          const pager = { ...pagination, current: 1 };
          loadData(pager);
          showSuccess("License has been renewed successfully.");
        })
        .catch((err) => {
          showError(err);
        });
    });
  };

  const columns = [
    {
      title: "Actions",
      width: "65px",
      align: "center",
      render: (text, record) => (
        <Button.Group>
          <Tooltip placement="topLeft" title="Renew">
            <Button
              type="primary"
              size="small"
              icon={<SyncOutlined />}
              onClick={() => onRenewLicense(record)}
              disabled={!(record.active && record.expired)}
            />
          </Tooltip>
          <Tooltip
            placement="topLeft"
            title={record.active ? "Disable" : "Enable"}
          >
            <Button
              type={!record.active ? "danger" : "primary"}
              size="small"
              icon={record.active ? <CheckCircleOutlined /> : <StopOutlined />}
              onClick={() => onActivate(record)}
            />
          </Tooltip>
          <Tooltip placement="topLeft" title="History">
            <Button
              size="small"
              icon={<EyeOutlined />}
              disabled={!record.renewed}
              onClick={() => {
                const { renewed } = record;
                if (renewed) {
                  navigate(`/devices/licenses/history`, {
                    state: record,
                  });
                }
              }}
            />
          </Tooltip>
        </Button.Group>
      ),
    },
    {
      title: "Customer name",
      dataIndex: "customerName",
      sorter: true,
    },
    {
      title: "Customer email",
      dataIndex: "customerEmail",
      sorter: true,
    },
    {
      title: "Distributor name",
      dataIndex: "companyName",
      sorter: true,
    },
    {
      title: "Days",
      dataIndex: "noOfDays",
      sorter: true,
    },
    {
      title: "Activation date",
      dataIndex: "activationDate",
      sorter: true,
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Expiry date",
      dataIndex: "expiryDate",
      sorter: true,
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
    {
      title: "Total devices",
      key: "totalDevices",
      render: (text, record) => {
        const total = record.devices.length;
        return (
          <Button
            size="small"
            type="link"
            onClick={() => {
              if (total > 0)
                setDeviceInfo({ visible: true, data: record.devices });
            }}
          >
            {total}
          </Button>
        );
      },
    },
    {
      title: "Authorization key",
      dataIndex: "authorizationKey",
      sorter: true,
    },
    {
      title: "Created by",
      dataIndex: "createdByName",
      sorter: true,
    },
    {
      title: "Created date",
      dataIndex: "createdOn",
      sorter: true,
      render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
    },
  ];

  const loadData = (pager, filters, sorter) => {
    setLoading(true);
    const gridify = getGridify(pager, {}, sorter, [
      {
        field: "expired",
        order: "descend",
      },
      {
        field: "expiryDate",
        order: "ascend",
      },
    ]);
    const args = { ...gridify, ...filters };
    get("api/v1/reports/licenseactivations", args)
      .then((resp) => {
        setData(resp.data);
        setPagination({
          ...pager,
          total: resp.count,
        });
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => setLoading(false));
  };

  const onTableChange = (pager, filters, sorter) => {
    const values = form.getFieldsValue();
    loadData(pager, values, sorter);
  };

  const onSearch = ({
    companyID,
    customerName,
    reportType,
    activationType,
  }) => {
    loadData(pagination, {
      companyID,
      customerName,
      reportType,
      activationType,
    });
  };

  const onClear = () => {
    form.resetFields();
    loadData(pagination);
  };

  useEffect(() => {
    get("api/v1/lookups/distributorslineage")
      .then((resp) => {
        setDistributors(resp);
      })
      .catch((err) => {
        showError(err);
      });
    loadData(pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={{ reportType: 1, activationType: 1 }}
        onFinish={onSearch}
      >
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item label="Distributors" name="companyID">
              <CSelect allowClear={true} dropdownMatchSelectWidth={false}>
                {distributors.map((r) => (
                  <Select.Option key={r.value} value={r.value}>
                    {r.text}
                  </Select.Option>
                ))}
              </CSelect>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="Customer name" name="customerName">
              <Input />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Expiring license" name="reportType">
              <CSelect>
                <Option key={1} value={1}>
                  All
                </Option>
                <Option key={2} value={2}>
                  Today
                </Option>
                <Option key={3} value={3}>
                  This week
                </Option>
                <Option key={4} value={4}>
                  This month
                </Option>
                <Option key={5} value={5}>
                  Expired
                </Option>
              </CSelect>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="Activation license" name="activationType">
              <CSelect>
                <Option key={1} value={1}>
                  All
                </Option>
                <Option key={2} value={2}>
                  Today
                </Option>
                <Option key={3} value={3}>
                  This week
                </Option>
                <Option key={4} value={4}>
                  This month
                </Option>
                <Option key={5} value={5}>
                  Not activated
                </Option>
              </CSelect>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="&nbsp;">
              <Space>
                <Button htmlType="submit" type="primary">
                  Search
                </Button>
                <Button htmlType="button" onClick={onClear}>
                  Clear
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CTable
        rowKey="licenseID"
        columns={columns}
        dataSource={data}
        pagination={pagination}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
      />

      {deviceInfo.visible && (
        <DeviceInfo
          data={deviceInfo.data}
          visible={deviceInfo.visible}
          onClose={() => {
            setDeviceInfo({ visible: false, data: [] });
          }}
        />
      )}
    </div>
  );
};
export default LicenseActivations;
